import React, { useState, useEffect, useRef } from 'react';
import { User, X } from 'lucide-react';
import api from '../utils/api';


const MentionDropdown = ({ 
  isVisible, 
  position, 
  query, 
  onSelectMention, 
  onClose 
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isVisible && query) {
      searchUsers(query);
    }
  }, [isVisible, query]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isVisible) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, users.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (users[selectedIndex]) {
            onSelectMention(users[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, users, selectedIndex, onSelectMention, onClose]);

  const searchUsers = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setUsers([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/team/users/search`, {
        params: { query: searchQuery },
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      setUsers(response.data || []);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Error searching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto min-w-64"
      style={{
        top: position.top + 'px',
        left: position.left + 'px',
      }}
    >
      {loading ? (
        <div className="p-3 text-center text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mx-auto"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="p-3 text-center text-gray-500 text-sm">
          {query ? 'No users found' : 'Start typing to search...'}
        </div>
      ) : (
        users.map((user, index) => (
          <div
            key={user.id}
            className={`flex items-center space-x-3 p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
              index === selectedIndex ? 'bg-purple-50' : 'hover:bg-gray-50'
            }`}
            onClick={() => onSelectMention(user)}
          >
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
              style={{ backgroundColor: user.avatar_color }}
            >
              {user.initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 text-sm truncate">
                {user.name}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {user.email}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const MentionSpan = ({ user, onClick, children }) => (
  <span
    className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity"
    style={{ 
      backgroundColor: user.avatar_color + '20', 
      color: user.avatar_color,
      border: `1px solid ${user.avatar_color}40`
    }}
    onClick={(e) => {
      e.preventDefault();
      onClick?.(user);
    }}
    data-mention-id={user.id}
    data-mention-name={user.name}
    contentEditable="false"
  >
    <User className="w-3 h-3 mr-1" />
    {children || user.name}
  </span>
);

const useMentionPlugin = (editorRef, onMentionClick) => {
  const [mentionState, setMentionState] = useState({
    isVisible: false,
    position: { top: 0, left: 0 },
    query: '',
    range: null
  });

  const handleMentionTrigger = (e) => {
    if (e.key === '@') {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setMentionState({
        isVisible: true,
        position: { 
          top: rect.bottom + window.scrollY + 5, 
          left: rect.left + window.scrollX 
        },
        query: '',
        range: range.cloneRange()
      });
    }
  };

  const handleInput = () => {
    if (!mentionState.isVisible) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) {
      closeMentionDropdown();
      return;
    }

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    
    if (textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent;
      const cursorPosition = range.startOffset;
      
      // Find the @ symbol before cursor
      let atIndex = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (text[i] === '@') {
          atIndex = i;
          break;
        }
        if (text[i] === ' ' || text[i] === '\n') {
          break;
        }
      }

      if (atIndex !== -1) {
        const query = text.substring(atIndex + 1, cursorPosition);
        setMentionState(prev => ({ ...prev, query }));
      } else {
        closeMentionDropdown();
      }
    }
  };

  const closeMentionDropdown = () => {
    setMentionState({
      isVisible: false,
      position: { top: 0, left: 0 },
      query: '',
      range: null
    });
  };

  const insertMention = (user) => {
    if (!mentionState.range) return;

    const selection = window.getSelection();
    const range = mentionState.range;
    
    // Find and remove the @query text
    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent;
      const cursorPosition = range.startOffset;
      
      // Find the @ symbol
      let atIndex = -1;
      for (let i = cursorPosition - 1; i >= 0; i--) {
        if (text[i] === '@') {
          atIndex = i;
          break;
        }
      }

      if (atIndex !== -1) {
        // Create mention element
        const mentionElement = document.createElement('span');
        mentionElement.className = 'inline-flex items-center px-2 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity';
        mentionElement.style.backgroundColor = user.avatar_color + '20';
        mentionElement.style.color = user.avatar_color;
        mentionElement.style.border = `1px solid ${user.avatar_color}40`;
        mentionElement.setAttribute('data-mention-id', user.id);
        mentionElement.setAttribute('data-mention-name', user.name);
        mentionElement.setAttribute('contentEditable', 'false');
        mentionElement.innerHTML = `<svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>${user.name}`;
        
        mentionElement.addEventListener('click', (e) => {
          e.preventDefault();
          onMentionClick?.(user);
        });

        // Replace the @query text with mention element
        const newRange = document.createRange();
        newRange.setStart(textNode, atIndex);
        newRange.setEnd(textNode, cursorPosition);
        
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        newRange.deleteContents();
        newRange.insertNode(mentionElement);
        
        // Add space after mention
        const spaceNode = document.createTextNode(' ');
        mentionElement.parentNode.insertBefore(spaceNode, mentionElement.nextSibling);
        
        // Move cursor after space
        const finalRange = document.createRange();
        finalRange.setStartAfter(spaceNode);
        finalRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(finalRange);
      }
    }

    closeMentionDropdown();
  };

  return {
    mentionState,
    handleMentionTrigger,
    handleInput,
    closeMentionDropdown,
    insertMention,
    MentionDropdown: () => (
      <MentionDropdown
        isVisible={mentionState.isVisible}
        position={mentionState.position}
        query={mentionState.query}
        onSelectMention={insertMention}
        onClose={closeMentionDropdown}
      />
    )
  };
};

// Utility function to extract mentions from HTML content
export const extractMentionsFromHTML = (htmlContent) => {
  if (!htmlContent) return [];
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  const mentionElements = doc.querySelectorAll('[data-mention-id]');
  
  return Array.from(mentionElements).map(element => ({
    userId: element.getAttribute('data-mention-id'),
    userName: element.getAttribute('data-mention-name'),
    mentionText: element.textContent
  }));
};

// Utility function to render mentions in display content
export const renderMentionsInContent = (htmlContent, onMentionClick) => {
  if (!htmlContent) return htmlContent;
  
  return htmlContent.replace(
    /<span[^>]*data-mention-id="([^"]*)"[^>]*data-mention-name="([^"]*)"[^>]*>(.*?)<\/span>/g,
    (match, userId, userName, content) => `
      <span 
        class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity mention-span" 
        data-mention-id="${userId}" 
        data-mention-name="${userName}"
        onclick="handleMentionClick('${userId}', '${userName}')"
      >
        ${content}
      </span>
    `
  );
};

export { MentionSpan, useMentionPlugin };
export default useMentionPlugin;